/**
 * Shared filter vocabulary + pure helpers for the diamond search filters
 * (desktop FilterSidebar + mobile FilterSheet). One source of truth for the
 * canonical option universes (VDB-parity, 2026-08-03), the finish (cut/
 * polish/symmetry) shortcut presets, carat preset bands, the KYC-gated
 * supplier facet parsing, and the URL <-> SearchFilters serialization.
 *
 * Rendering stays FACET-GATED everywhere: extending a list here does not put
 * dead chips on screen — a value shows up the moment inventory carries it.
 */
import type { SearchFilters } from '../api/types'

// ── Canonical option universes ──────────────────────────────────────────────

// Shapes: ONE source of truth shared with the backend mappers/facets —
// functions/src/shared/shapes.ts (dependency-free, browser-safe). The
// backend's mapShape is unit-tested to emit ONLY these labels, so every
// facet key the API serves matches a chip here by construction.
export { SHAPES_CORE, SHAPES_EXTENDED } from '../../../functions/src/shared/shapes'
import { browseParamsFromFilters, filtersFromBrowseParams } from '../../../functions/src/shared/filterCatalog'

/** White grades. Core D–J covers ~100% of today's stock; K–Z is the See-more tail. */
export const COLORS_CORE = ['D', 'E', 'F', 'G', 'H', 'I', 'J']
export const COLORS_EXTENDED = ['K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

/** Fancy hues — multi-select now (fancyHues[], OR server-side). Facet-gated. */
export const FANCY_HUES = ['Yellow', 'Pink', 'Blue', 'Green', 'Orange', 'Brown', 'Purple', 'Red', 'Gray', 'Violet']
/** Fancy intensity grades, lightest → deepest. Bare "Fancy" = unqualified grade. */
export const FANCY_INTENSITIES = ['Fancy Light', 'Fancy', 'Fancy Intense', 'Fancy Vivid', 'Fancy Deep', 'Fancy Dark']

export const CLARITIES_CORE = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2']
export const CLARITIES_EXTENDED = ['SI3', 'I1', 'I2', 'I3']

/** Grade ladder shared by Cut / Polish / Symmetry (best→worst). */
export const GRADES = ['Ideal', 'Excellent', 'Very Good', 'Good', 'Fair']
export const FLUO_ORDER = ['None', 'Faint', 'Slight', 'Medium', 'Strong', 'Very Strong']
export const GROWTH_ORDER = ['CVD', 'HPHT', 'Natural']
/** Common lab-grown certifying labs (canonical order while facets load). "None" =
 *  uncertified — always offered, like Natural in growth (a real buyer preference). */
export const LAB_FALLBACK = ['IGI', 'GIA', 'GCAL', 'HRD', 'SGL']

// ── Carat preset bands (VDB-style quick picks) ──────────────────────────────

export interface CaratPreset { label: string; min: number; max?: number }
/** The buyer-common 1ct+ bands, always visible. */
export const CARAT_PRESETS_CORE: CaratPreset[] = [
  { label: '1.00–1.49', min: 1.0, max: 1.49 },
  { label: '1.50–1.99', min: 1.5, max: 1.99 },
  { label: '2.00–2.99', min: 2.0, max: 2.99 },
  { label: '3.00+', min: 3.0 },
]
/** The finer VDB tail, under See more. */
export const CARAT_PRESETS_EXTENDED: CaratPreset[] = [
  { label: '0.30–0.39', min: 0.3, max: 0.39 },
  { label: '0.40–0.49', min: 0.4, max: 0.49 },
  { label: '0.50–0.69', min: 0.5, max: 0.69 },
  { label: '0.70–0.89', min: 0.7, max: 0.89 },
  { label: '0.90–0.99', min: 0.9, max: 0.99 },
  { label: '3.00–3.99', min: 3.0, max: 3.99 },
  { label: '4.00–4.99', min: 4.0, max: 4.99 },
  { label: '5.00+', min: 5.0 },
]

export function caratPresetActive(f: SearchFilters, p: CaratPreset): boolean {
  return f.caratMin === p.min && (p.max === undefined ? f.caratMax == null : f.caratMax === p.max)
}

/** Single-select toggle semantics (same as the mobile range presets): picking a
 *  band sets min/max; picking it again clears both. */
export function toggleCaratPreset(f: SearchFilters, p: CaratPreset): SearchFilters {
  return caratPresetActive(f, p)
    ? { ...f, caratMin: undefined, caratMax: undefined }
    : { ...f, caratMin: p.min, caratMax: p.max }
}

// ── Finish shortcuts (3EX+ / 3VG+) ──────────────────────────────────────────
// Pure UI presets over the existing cut/polish/symmetry arrays — no backend
// key. Ideal outranks Excellent, so "EX or better" includes it. VDB's "8X"
// (IGI's 8-parameter program) is omitted: no feed supplies that flag.

export const FINISH_SHORTCUTS = {
  '3EX+': { cut: ['Ideal', 'Excellent'], polish: ['Excellent'], symmetry: ['Excellent'] },
  '3VG+': { cut: ['Ideal', 'Excellent', 'Very Good'], polish: ['Excellent', 'Very Good'], symmetry: ['Excellent', 'Very Good'] },
} as const
export type FinishShortcutKey = keyof typeof FINISH_SHORTCUTS

const sameSet = (a: string[] | undefined, b: readonly string[]): boolean => {
  const set = new Set(a || [])
  return set.size === b.length && b.every((v) => set.has(v))
}

export function finishShortcutActive(f: SearchFilters, k: FinishShortcutKey): boolean {
  const s = FINISH_SHORTCUTS[k]
  return sameSet(f.cut, s.cut) && sameSet(f.polish, s.polish) && sameSet(f.symmetry, s.symmetry)
}

export function toggleFinishShortcut(f: SearchFilters, k: FinishShortcutKey): SearchFilters {
  if (finishShortcutActive(f, k)) return { ...f, cut: undefined, polish: undefined, symmetry: undefined }
  const s = FINISH_SHORTCUTS[k]
  return { ...f, cut: [...s.cut], polish: [...s.polish], symmetry: [...s.symmetry] }
}

// ── Supplier facet (KYC-gated) ──────────────────────────────────────────────

export interface SupplierFacet { id: number; name: string; count: number }

/** Parse the server's "id:name" → count supplier facet. Returns null when the
 *  dimension is absent (anon / un-KYC'd callers — the section stays hidden). */
export function parseSupplierFacets(dim?: Record<string, number>): SupplierFacet[] | null {
  if (!dim) return null
  const out: SupplierFacet[] = []
  for (const [key, count] of Object.entries(dim)) {
    const i = key.indexOf(':')
    if (i <= 0) continue
    const id = Number(key.slice(0, i))
    if (!Number.isFinite(id)) continue
    out.push({ id, name: key.slice(i + 1) || `Supplier ${id}`, count })
  }
  return out.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/** Available fancy intensity grades from the color facet keys ("Fancy Vivid
 *  Blue" → "Fancy Vivid"; unqualified "Fancy Yellow" → "Fancy"). */
export function intensitiesFromColorFacet(colorDim?: Record<string, number>): Set<string> {
  const found = new Set<string>()
  const qualified = FANCY_INTENSITIES.filter((g) => g !== 'Fancy')
  for (const key of Object.keys(colorDim || {})) {
    const c = key.trim().toLowerCase()
    if (!c.startsWith('fancy')) continue
    const grade = qualified.find((g) => c.startsWith(g.toLowerCase()))
    found.add(grade ?? 'Fancy')
  }
  return found
}

// ── URL params <-> filters serialization ────────────────────────────────────
// Shared by desktop /search and (via filterData) deep-links into mobile
// /browse. Every UI-settable filter must round-trip or shared links lose it.

// The param <-> key mapping DERIVES from the shared filter catalog
// (functions/src/shared/filterCatalog.ts, browser-safe like shapes.ts) — the
// same table the API's FilterSchema and Gemma's search tool build from, so a
// chat "Browse all matches" link and a sidebar-built URL can never drift.

export function filtersFromParams(p: URLSearchParams): SearchFilters {
  const { sortBy: _sort, ...filters } = filtersFromBrowseParams((k) => p.get(k)) as SearchFilters
  // sortBy stays OUT of the filter object — callers (Search.tsx) manage the
  // sort as separate state and read the 'sort' param themselves.
  return filters
}

export function paramsFromFilters(f: SearchFilters): URLSearchParams {
  const p = new URLSearchParams()
  for (const [k, v] of browseParamsFromFilters(f as Record<string, unknown>)) p.set(k, v)
  return p
}
